import { ref } from "vue";
import { useListingStore } from "@/stores/listing";
import { Errors, ListingBid } from "@/types";
import { hideModal, showModal } from "@/util/dialog";

export interface UseCancelBidOptions {
  /** id of a <dialog> to open on request and close on completion. */
  dialogId?: string;
  /** Called after every attempt, success or failure, so callers can reload. */
  onCancelled?: () => void | Promise<void>;
}

/**
 * Stage → confirm → cancel flow for one of the user's own active bids.
 * Shared by the auction page and the account bid history page.
 */
export function useCancelBid(
  slug: () => string,
  options: UseCancelBidOptions = {},
) {
  const listingStore = useListingStore();

  const pendingBid = ref<ListingBid | null>(null);
  const cancelling = ref(false);
  const cancelError = ref<Errors.CancelBidError | null>(null);

  function requestCancel(bid: ListingBid) {
    cancelError.value = null;
    pendingBid.value = bid;

    if (options.dialogId) {
      showModal(options.dialogId, () => {
        if (!cancelling.value) {
          pendingBid.value = null;
        }
      });
    }
  }

  function dismissCancel() {
    pendingBid.value = null;

    if (options.dialogId) {
      hideModal(options.dialogId);
    }
  }

  async function confirmCancel() {
    const bid = pendingBid.value;
    if (!bid || cancelling.value) {
      return;
    }

    cancelling.value = true;

    try {
      await listingStore.cancelBid(slug(), bid.id);
    } catch (error) {
      cancelError.value =
        error instanceof Errors.CancelBidError
          ? error
          : new Errors.CancelBidError("This bid could not be cancelled.");
    } finally {
      cancelling.value = false;
      dismissCancel();
    }

    await options.onCancelled?.();
  }

  return {
    pendingBid,
    cancelling,
    cancelError,
    requestCancel,
    confirmCancel,
    dismissCancel,
  };
}
